import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, Loader, EmptyState } from "@/components/common";
import { CalendarDays, Plus, Check, X, Trash2, ChevronLeft, ChevronRight, Clock } from "lucide-react";

const PLATFORMS = ["YouTube", "YouTube Shorts", "TikTok", "Instagram Reels"];
const platformDot = {
  "YouTube": "bg-brand", "YouTube Shorts": "bg-brand",
  "TikTok": "bg-white", "Instagram Reels": "bg-signal-blue",
};
const statusStyle = {
  scheduled: "text-signal-yellow border-signal-yellow/30 bg-signal-yellow/10",
  posted: "text-signal-green border-signal-green/30 bg-signal-green/10",
  cancelled: "text-white/40 border-white/15 bg-white/5 line-through",
};

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const fmtTime = (iso) => new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });

export default function CalendarPage() {
  const [items, setItems] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pkgId, setPkgId] = useState("");
  const [platform, setPlatform] = useState("YouTube");
  const [when, setWhen] = useState("");
  const [note, setNote] = useState("");
  const [cursor, setCursor] = useState(new Date());

  const load = () => api.get("/schedule").then((r) => setItems(r.data.schedule)).finally(() => setLoading(false));
  useEffect(() => {
    load();
    api.get("/content").then((r) => setPackages(r.data.content));
  }, []);

  const add = async () => {
    if (!pkgId) { toast.error("Pick a video package"); return; }
    if (!when) { toast.error("Pick a date & time"); return; }
    try {
      const r = await api.post("/schedule", { package_id: pkgId, platform, scheduled_at: new Date(when).toISOString(), note });
      setItems((p) => [...p, r.data].sort((a, b) => a.scheduled_at.localeCompare(b.scheduled_at)));
      toast.success(`Scheduled for ${platform}`);
      setNote(""); setWhen("");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to schedule"); }
  };

  const setStatus = async (id, status) => {
    await api.patch(`/schedule/${id}`, { status });
    setItems((p) => p.map((x) => (x.id === id ? { ...x, status } : x)));
  };
  const del = async (id) => {
    await api.delete(`/schedule/${id}`);
    setItems((p) => p.filter((x) => x.id !== id));
  };

  const year = cursor.getFullYear(), month = cursor.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  const itemsOnDay = (d) => items.filter((x) => {
    const dt = new Date(x.scheduled_at);
    return dt.getFullYear() === year && dt.getMonth() === month && dt.getDate() === d;
  });
  const today = new Date();
  const isToday = (d) => today.getFullYear() === year && today.getMonth() === month && today.getDate() === d;

  const upcoming = items.filter((x) => x.status === "scheduled" && new Date(x.scheduled_at) >= new Date()).slice(0, 12);

  return (
    <div className="p-6 lg:p-10 max-w-7xl">
      <PageHeader eyebrow="Plan & Publish" title="Content Calendar" testid="calendar-header"
        subtitle="Schedule your videos and short-form variants across platforms. YouTube uploads can auto-publish at the scheduled time." />

      <div className="bg-surface border border-white/10 rounded-md p-5 mb-6 fade-up">
        <div className="text-[11px] uppercase tracking-[0.2em] text-white/40 font-mono mb-4 flex items-center gap-2"><Plus className="w-3.5 h-3.5" /> Schedule a post</div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 md:items-end">
          <div className="md:col-span-4">
            <label className="text-xs uppercase tracking-wider text-white/40 mb-2 block font-mono">Video Package</label>
            <select data-testid="schedule-package-select" value={pkgId} onChange={(e) => setPkgId(e.target.value)}
              className="w-full bg-[#0A0A0A] border border-white/10 rounded-md px-3 py-2.5 outline-none focus:border-brand text-sm">
              <option value="" className="bg-[#121212]">Select a package…</option>
              {packages.map((p) => <option key={p.id} value={p.id} className="bg-[#121212]">{(p.titles && p.titles[0]) || p.topic}</option>)}
            </select>
          </div>
          <div className="md:col-span-3">
            <label className="text-xs uppercase tracking-wider text-white/40 mb-2 block font-mono">Platform</label>
            <select data-testid="schedule-platform-select" value={platform} onChange={(e) => setPlatform(e.target.value)}
              className="w-full bg-[#0A0A0A] border border-white/10 rounded-md px-3 py-2.5 outline-none focus:border-brand text-sm">
              {PLATFORMS.map((p) => <option key={p} value={p} className="bg-[#121212]">{p}</option>)}
            </select>
          </div>
          <div className="md:col-span-3">
            <label className="text-xs uppercase tracking-wider text-white/40 mb-2 block font-mono">When</label>
            <input type="datetime-local" data-testid="schedule-datetime" value={when} onChange={(e) => setWhen(e.target.value)}
              className="w-full bg-[#0A0A0A] border border-white/10 rounded-md px-3 py-2.5 outline-none focus:border-brand text-sm [color-scheme:dark]" />
          </div>
          <div className="md:col-span-2">
            <button data-testid="schedule-add-btn" onClick={add}
              className="w-full bg-brand hover:bg-brand-hover text-white font-medium rounded-md px-4 py-2.5 text-sm transition-colors">Schedule</button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-surface border border-white/10 rounded-md p-5 fade-up">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-xl tracking-tight">{MONTHS[month]} {year}</h3>
            <div className="flex gap-1">
              <button data-testid="cal-prev" onClick={() => setCursor(new Date(year, month - 1, 1))} className="border border-white/10 hover:border-white/30 rounded-md p-1.5 transition-colors"><ChevronLeft className="w-4 h-4" /></button>
              <button data-testid="cal-next" onClick={() => setCursor(new Date(year, month + 1, 1))} className="border border-white/10 hover:border-white/30 rounded-md p-1.5 transition-colors"><ChevronRight className="w-4 h-4" /></button>
            </div>
          </div>
          <div className="grid grid-cols-7 gap-1 mb-1">
            {["S","M","T","W","T","F","S"].map((d, i) => <div key={i} className="text-center text-[10px] font-mono text-white/30 py-1">{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {cells.map((d, i) => (
              <div key={i} className={`aspect-square rounded border p-1 ${d ? "border-white/5" : "border-transparent"} ${isToday(d) ? "border-brand/50 bg-brand/5" : ""}`}>
                {d && (
                  <>
                    <div className={`text-[10px] font-mono ${isToday(d) ? "text-brand" : "text-white/40"}`}>{d}</div>
                    <div className="flex flex-wrap gap-0.5 mt-0.5">
                      {itemsOnDay(d).slice(0, 4).map((x) => <span key={x.id} title={`${x.platform} · ${x.title}`} className={`w-1.5 h-1.5 rounded-full ${x.status === "cancelled" ? "bg-white/15" : platformDot[x.platform] || "bg-white/40"}`} />)}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="bg-surface border border-white/10 rounded-md p-5 fade-up">
          <h3 className="font-heading text-xl tracking-tight mb-4 flex items-center gap-2"><Clock className="w-4 h-4 text-signal-yellow" /> Upcoming</h3>
          {loading ? <Loader label="Loading" /> : upcoming.length === 0 ? (
            <p className="text-white/40 text-sm">Nothing scheduled yet. Add a post above.</p>
          ) : (
            <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
              {upcoming.map((x) => (
                <div key={x.id} className="bg-[#0A0A0A] border border-white/5 rounded p-3" data-testid={`schedule-item-${x.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm truncate">{x.title}</div>
                      <div className="text-[10px] font-mono text-white/40 mt-1 flex items-center gap-1.5">
                        <span className={`w-1.5 h-1.5 rounded-full ${platformDot[x.platform] || "bg-white/40"}`} />{x.platform} · {fmtTime(x.scheduled_at)}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-1 mt-2">
                    <button onClick={() => setStatus(x.id, "posted")} data-testid={`mark-posted-${x.id}`} className="text-[10px] font-mono border border-signal-green/30 text-signal-green hover:bg-signal-green/10 rounded px-2 py-1 transition-colors flex items-center gap-1"><Check className="w-3 h-3" /> Posted</button>
                    <button onClick={() => setStatus(x.id, "cancelled")} data-testid={`cancel-${x.id}`} className="text-[10px] font-mono border border-white/10 text-white/50 hover:text-white rounded px-2 py-1 transition-colors flex items-center gap-1"><X className="w-3 h-3" /> Cancel</button>
                    <button onClick={() => del(x.id)} data-testid={`del-schedule-${x.id}`} className="text-[10px] font-mono border border-white/10 text-white/40 hover:text-brand hover:border-brand/30 rounded px-2 py-1 transition-colors ml-auto"><Trash2 className="w-3 h-3" /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="mt-6 fade-up">
        <h3 className="font-heading text-lg tracking-tight mb-3">All Scheduled</h3>
        {items.length === 0 ? (
          <EmptyState icon={CalendarDays} title="No scheduled posts" hint="Schedule your first post above to start planning your content pipeline." />
        ) : (
          <div className="border border-white/10 rounded-md overflow-hidden">
            {items.map((x) => (
              <div key={x.id} className="flex items-center gap-4 px-4 py-3 border-b border-white/5 last:border-0 hover:bg-white/5 transition-colors" data-testid={`schedule-row-${x.id}`}>
                <span className={`w-2 h-2 rounded-full shrink-0 ${platformDot[x.platform] || "bg-white/40"}`} />
                <span className="text-sm truncate flex-1">{x.title}</span>
                <span className="text-xs font-mono text-white/40 hidden sm:block w-32 truncate">{x.platform}</span>
                <span className="text-xs font-mono text-white/40 hidden md:block w-40">{fmtTime(x.scheduled_at)}</span>
                <span className={`text-[10px] uppercase tracking-wider font-mono px-2 py-1 rounded border ${statusStyle[x.status]}`}>{x.status}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
